import { useState, useEffect, useCallback } from "react";
import { TrendingUp, MapPin, Target, RefreshCw, Search } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MarketDemandWidget = ({ authHeader }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/v1/ai-search/insights?days=${days}`, {
        headers: { Authorization: authHeader },
      });
      setData(res.data);
    } catch (e) {
      console.error("Failed to load search insights", e);
    } finally {
      setLoading(false);
    }
  }, [authHeader, days]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const ListCard = ({ title, icon: Icon, rows, emptyText, renderRow }) => (
    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#00E5FF]" />
        <h3 className="text-sm font-semibold text-white">{title}</h3>
      </div>
      {rows?.length ? (
        <div className="space-y-2">{rows.map(renderRow)}</div>
      ) : (
        <p className="text-xs text-[#64748B]">{emptyText}</p>
      )}
    </div>
  );

  return (
    <div className="glass-card p-6 rounded-2xl mb-8" data-testid="market-demand-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Search className="w-6 h-6 text-[#00E5FF]" />
          <div>
            <h2 className="text-xl font-bold font-['Outfit'] text-white">AI Search — Market Demand</h2>
            <p className="text-xs text-[#94A3B8]">What people search for, and where the Best of Best slots are open</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-[#0F172A] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white"
            data-testid="market-demand-days-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={fetchInsights}
            className="p-2 rounded-lg bg-white/5 border border-white/10 text-[#94A3B8] hover:text-white transition-colors"
            data-testid="market-demand-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[
              { label: "Total Searches", value: data.totals.searches, color: "text-white" },
              { label: "Had Best of Best", value: data.totals.with_best_of_best, color: "text-[#F59E0B]" },
              { label: "No Best of Best", value: data.totals.without_best_of_best, color: "text-[#EC4899]" },
              { label: "Zero Results", value: data.totals.zero_results, color: "text-[#EF4444]" },
            ].map((s) => (
              <div key={s.label} className="bg-white/[0.03] border border-white/10 rounded-xl p-3 text-center">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-[10px] text-[#94A3B8] uppercase tracking-wide">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ListCard
              title="Top Searched Categories"
              icon={TrendingUp}
              rows={data.top_categories}
              emptyText="No searches logged yet."
              renderRow={(r, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-[#CBD5E1] capitalize truncate">{r.name}</span>
                  <span className="text-white font-medium ml-2">{r.count}</span>
                </div>
              )}
            />
            <ListCard
              title="Top Searched Cities"
              icon={MapPin}
              rows={data.top_cities}
              emptyText="No city-level searches yet."
              renderRow={(r, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-[#CBD5E1] truncate">{r.name}</span>
                  <span className="text-white font-medium ml-2">{r.count}</span>
                </div>
              )}
            />
            <ListCard
              title="Opportunity Report — No Best of Best"
              icon={Target}
              rows={data.opportunities}
              emptyText="No open-slot searches yet."
              renderRow={(r, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-[#CBD5E1] capitalize truncate">
                    {r.category}{r.city ? ` · ${r.city}` : ""}
                  </span>
                  <span className="text-[#EC4899] font-medium ml-2">{r.count}×</span>
                </div>
              )}
            />
          </div>

          {data.recent_searches?.length > 0 && (
            <div className="mt-4 bg-white/[0.03] border border-white/10 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-white mb-3">Recent Searches</h3>
              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {data.recent_searches.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs gap-3">
                    <span className="text-[#CBD5E1] truncate">"{s.search_query}"</span>
                    <span className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[#64748B]">{s.results_count} results</span>
                      {s.had_best_of_best ? (
                        <span className="text-[#F59E0B]">BoB ✓</span>
                      ) : (
                        <span className="text-[#EC4899]">open slot</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {!data && loading && <p className="text-sm text-[#94A3B8]">Loading search insights...</p>}
    </div>
  );
};

export default MarketDemandWidget;
